/**
 * Hopper — periodic deterministic jumper (Brief 008 archetype; species: frog).
 *
 * Sits grounded, charging a jump on a FIXED-STEP interval (a `dt`-accumulated timer,
 * no wall-clock / RNG → replay-identical by construction). On fire it launches a
 * ballistic arc in its facing direction (`vy = -hopImpulse`, `vx = facing*hopHSpeed`);
 * gravity brings it down; on landing it resets to a grounded wait. Reverses at walls,
 * and (when grounded, before a hop) at ledges so it doesn't blindly hop into pits.
 *
 * Sprites: `<variant>_rest` while grounded/idle, `<variant>_jump` while airborne,
 * `<variant>_flat` once stomped.
 *
 * Combat: stomp from above kills + bounces (no heart cost); other contact hurts.
 *
 * Determinism: floor/abs/min/max only. No transcendentals, no random, no Date.now.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

const GRAVITY = 2050;
const TERMINAL = 1200;
const DEFAULT_HOP_INTERVAL = 1.4; // seconds grounded between hops
const DEFAULT_HOP_IMPULSE = 620;  // px/s upward launch velocity
const DEFAULT_HOP_HSPEED = 90;    // px/s horizontal launch velocity
const SQUASH_SECONDS = 0.4;

export class Hopper implements Entity {
  readonly type = EntityType.HOPPER;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = -1;
  readonly spriteFacesLeft = true; // Kenney frog art faces left
  alive = true;
  squashTimer = 0;

  private variant: string;
  private hopInterval: number;
  private hopImpulse: number;
  private hopHSpeed: number;
  private turnAtLedge: boolean;
  private grounded = false;
  private hopTimer = 0; // dt-accumulated grounded time toward the next hop

  constructor(cx: number, feetY: number, props?: Record<string, unknown>, w = 50, h = 46) {
    this.body = { x: cx - w / 2, y: feetY - h, w, h };

    const variant = props?.variant;
    this.variant = typeof variant === 'string' && variant.length ? variant : 'frog';
    this.hopInterval = typeof props?.hopInterval === 'number' ? props.hopInterval : DEFAULT_HOP_INTERVAL;
    this.hopImpulse = typeof props?.hopImpulse === 'number' ? props.hopImpulse : DEFAULT_HOP_IMPULSE;
    this.hopHSpeed = typeof props?.hopHSpeed === 'number' ? props.hopHSpeed : DEFAULT_HOP_HSPEED;
    this.turnAtLedge = props?.turnAtLedge !== false;

    const dir = props?.dir;
    if (dir === 1 || dir === '1' || dir === 'right') this.facing = 1;
    else if (dir === -1 || dir === '-1' || dir === 'left') this.facing = -1;
  }

  update(dt: number, ctx: EntityContext): void {
    if (!this.alive) { this.squashTimer = Math.max(0, this.squashTimer - dt); return; }
    const grid = ctx.grid;

    // gravity + ground (track grounded transition)
    this.vy = Math.min(this.vy + GRAVITY * dt, TERMINAL);
    const landed = grid.collideY(this.body, this.vy * dt, false).grounded;
    if (landed) {
      this.vy = 0;
      if (!this.grounded) { this.vx = 0; this.hopTimer = 0; } // just landed → reset to wait
      this.grounded = true;
    } else {
      this.grounded = false;
    }

    if (this.grounded) {
      // Charge the next hop on a fixed-step clock.
      this.hopTimer += dt;
      if (this.hopTimer >= this.hopInterval) {
        // Before launching, optionally turn away from a ledge so we land on ground.
        if (this.turnAtLedge && !this.groundAhead(grid)) this.facing = this.facing > 0 ? -1 : 1;
        this.vy = -this.hopImpulse;
        this.vx = this.facing * this.hopHSpeed;
        this.hopTimer = 0;
        this.grounded = false;
      }
    } else if (this.vx !== 0) {
      // Airborne arc: carry horizontal, reverse on a wall.
      if (grid.collideX(this.body, this.vx * dt)) {
        this.facing = this.facing > 0 ? -1 : 1;
        this.vx = this.facing * this.hopHSpeed;
      }
    }
  }

  /** Solid ground ~1 tile ahead in the facing direction (so a hop won't sail into a pit). */
  private groundAhead(grid: { solidBelowPoint(x: number, y: number): boolean }): boolean {
    const aheadX = this.facing > 0 ? this.body.x + this.body.w + 40 : this.body.x - 40;
    const belowY = this.body.y + this.body.h + 8;
    return grid.solidBelowPoint(aheadX, belowY);
  }

  onPlayerOverlap(ctx: EntityContext): void {
    const p = ctx.player.body;
    const playerFeet = p.y + p.h;
    // Stomp only with offense (CLASSIC). PRECISION (no offense) → would-be stomp hurts.
    const fallingOnto = ctx.player.vy > 0 && playerFeet - this.body.y < this.body.h * 0.7;
    if (fallingOnto && ctx.offenseEnabled) {
      ctx.killEntity(this);
      ctx.bouncePlayer();
      ctx.playSound('stomp', 0.5); // POLISH_01 #1 — dedicated stomp clip
    } else {
      ctx.hurtPlayer();
    }
  }

  onKilled(): void {
    if (!this.alive && this.squashTimer > 0) return;
    this.alive = false;
    this.squashTimer = SQUASH_SECONDS;
    this.vx = 0;
    this.vy = 0;
  }

  spriteKey(): string {
    if (!this.alive) return `${this.variant}_flat`;
    return this.grounded ? `${this.variant}_rest` : `${this.variant}_jump`;
  }

  visible(): boolean { return this.alive || this.squashTimer > 0; }
}
